/**
 * PeoplePrintDocument — overlay d'aperçu + déclencheur d'impression.
 *
 * Approche pagination : CSS Paged Media natif (cf. print/PeoplePrint.css).
 * Le navigateur gère la pagination des tableaux longs grâce à :
 *   - thead { display: table-header-group }   → en-tête répété sur chaque page
 *   - tfoot { display: table-footer-group }   → totaux en pied du tableau
 *   - tr   { break-inside: avoid }            → pas de ligne coupée
 *   - .ppl-print__section { break-before: page } → saut entre rapports
 *
 * Pas de mesure JS, pas de slicing manuel. Si le tableau ne tient pas, le
 * navigateur ajoute des pages automatiquement.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import HrSynthesisCockpitPrint from './print/HrSynthesisCockpitPrint';
import HrEvolutionPrint  from './print/HrEvolutionPrint';
import HrYearEndPrint    from './print/HrYearEndPrint';
import LcrPrint          from './print/LcrPrint';
import './print/PeoplePrint.css';
import './PeoplePrintDocument.css';

const TOTAL_REPORTS = 4;

export default function PeoplePrintDocument({ activeTenant, onClose }) {
  const { t } = useTranslation();
  const p = useP();
  const [ready, setReady] = useState(false);
  const [doneReports, setDoneReports] = useState(0);

  const year = new Date().getFullYear();
  const editedAt = useMemo(() => new Date(), []);

  // body class pour les règles @media print
  useEffect(() => {
    document.body.classList.add('people-printing');
    return () => document.body.classList.remove('people-printing');
  }, []);

  // Ferme automatiquement après impression
  useEffect(() => {
    const handler = () => onClose();
    window.addEventListener('afterprint', handler);
    return () => window.removeEventListener('afterprint', handler);
  }, [onClose]);

  // Fallback : si les 3 rapports n'ont pas notifié dans 4s, on autorise quand même l'impression
  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  // Passe à ready dès que les 3 reports ont notifié
  useEffect(() => {
    if (doneReports >= TOTAL_REPORTS) setReady(true);
  }, [doneReports]);

  const handleDataReady = () => setDoneReports(n => n + 1);

  // Footer print (--ppl-footer-left injecté dans @page via CSS var)
  const footerLeft = `${(activeTenant?.name || '').toUpperCase()} · ${editedAt.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
  const cssVarStyle = { '--ppl-footer-left': `"${footerLeft.replace(/"/g, '\\"')}"` };

  const handlePrint = () => window.print();

  const dateStr = editedAt.toLocaleDateString('fr-CH', { day: '2-digit', month: 'long', year: 'numeric' });

  return createPortal(
    <div className="ppl-print-overlay" style={cssVarStyle}>
      <div className="ppl-print-controls">
        <div className="ppl-print-controls__left">
          <span className="ppl-print-controls__title">
            {p('Aperçu — People reports · ')}{activeTenant?.name}
          </span>
          {!ready && (
            <span className="ppl-print-controls__loading">
              ⟳ {t('common:status.loading').replace(/…$/, '')} ({doneReports}/{TOTAL_REPORTS})…
            </span>
          )}
        </div>
        <div className="ppl-print-controls__right">
          <button
            className="ppl-print-btn ppl-print-btn--print"
            onClick={handlePrint}
            disabled={!ready}
            title={ready ? p('Imprimer ou sauvegarder en PDF') : p('Patientez…')}
          >
            {ready ? `⎙ ${p('Imprimer / PDF')}` : `⟳ ${t('common:status.loading')}`}
          </button>
          <button className="ppl-print-btn ppl-print-btn--close" onClick={onClose}>
            ✕ {t('common:actions.close')}
          </button>
        </div>
      </div>

      <div className="ppl-print__preview">
        <div className="ppl-print">
          <div className="ppl-cover">
            <div className="ppl-cover__brand">Palestre</div>
            <h1 className="ppl-cover__title">{p('Charges de personnel')}</h1>
            <div className="ppl-cover__meta">{activeTenant?.name} · {year}</div>
            <div className="ppl-cover__date">{p('Édité le ')}{dateStr}</div>
            <div className="ppl-cover__toc">
              <div className="ppl-cover__toc-title">{p('Sommaire')}</div>
              <ol className="ppl-cover__toc-list">
                <li>{p('Synthèse — masse salariale (à date → atterrissage)')}</li>
                <li>{p('Effectif — grille mensuelle par personne')}</li>
                <li>{p('Évolution des effectifs et de la masse salariale')}</li>
                <li>{p('Salaire horaire (LCR) par personne')}</li>
              </ol>
            </div>
          </div>
          <HrSynthesisCockpitPrint
            activeTenant={activeTenant}
            year={year}
            editedAt={editedAt}
            onDataReady={handleDataReady}
          />
          <HrYearEndPrint
            activeTenant={activeTenant}
            year={year}
            editedAt={editedAt}
            onDataReady={handleDataReady}
          />
          <HrEvolutionPrint
            activeTenant={activeTenant}
            refYear={year}
            editedAt={editedAt}
            onDataReady={handleDataReady}
          />
          <LcrPrint
            activeTenant={activeTenant}
            year={year}
            editedAt={editedAt}
            onDataReady={handleDataReady}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
